/**
 * testingSlice — Redux slice для процесса тестирования (прохождения теста)
 * 
 * Отвечает за:
 * - Хранение данных теста (вопросы, варианты ответов)
 * - Хранение ответов пользователя
 * - Навигацию между вопросами
 * - Сохранение прогресса в localStorage
 * - Синхронизацию revision (сброс прогресса при изменении теста)
 */

import { apiUrl } from '@/api/axios.js';
import {
    checkIfLocalStorageDoesNotContainCurrentState
} from '@/core/utils/checkIfLocalStorageDoesNotContainCurrentState.js';
import { handleResponseError } from '@/core/utils/handleResponseError.js';
import { resetDurationsState } from '@/store/reducers/durationSlice.js';
import { resetLikesState } from '@/store/reducers/likesSlice.js';
import { setReportFormEmail } from '@/store/reducers/reportFormSlice';
import { getTestStorageKeys } from '@/core/utils/testStorageKeys';
import { resolveOrderConfig } from '@/components/features/testing/OrderAnswer/resolveOrderConfig';
import type { AppDispatch, RootState } from '@/store/store';
import type {
    AnswerValue,
    AutoAnswerRule,
    QuestionAnswer,
    TestAdditional,
    TestContextPayload,
    TestingState,
    TestQuestion,
} from '@/store/types/testingTypes';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import saveDataToLocalStorage, { setLocalStorage } from '../../core/utils/saveDataToLocalStorage.js';

// ============================================
// Начальное состояние
// ============================================

const initialState: TestingState = {
    /** Массив вопросов теста (приходит с API) */
    testData: [],
    testId: null,
    /** Ключ виджета (?wgt=...), пробрасывается в /api/results, /api/lastAttempt, /api/attempts */
    widgetKey: null,
    /** Ключ группового теста (?g=...), пробрасывается в /api/results */
    groupKey: null,
    /** Session id виджета (?sid=...), пробрасывается в /api/results */
    sid: null,
    name: '',

    /** Массив ответов пользователя: [{ question_id, answer: [{ name, val }] }] */
    answers: [],

    /** id вопросов, заполненных авто-ответом (auto_answer) — для скрытия «Назад» */
    autoFilledQuestionIds: [],

    /** Цветовая тема теста (для фона) */
    background: null,

    /** Дополнительные настройки теста (реклама, блоки UI) */
    additional: {
        // todo реализована только реклама в блоках как conditional
        blocks: {
            "processProgressCounter": { "show": false, "content": "" },
            "processCloseBtn": { "show": false, "content": "" },
            "processProgressBar": { "show": false, "content": "" },
            "processLike": { "show": false, "content": "" },
            "processReportForm": { "show": false, "content": "" },
            "processAdvFullscreen": { "show": false, "content": "" },
            "processAdvBottom": { "show": false, "content": "" }
        },
        device: {
            country: '', // страна пользователя (для рекламы)
        },
    },

    /** Индекс текущего вопроса (0-based) */
    activeQuestion: 0,

    /** Состояние выбора ответа (для анимации) */
    answerState: {
        isSelected: false,
    },

    /** Ключ для localStorage где хранятся ответы: `test_${testId}_answers` */
    localStorageKey: '',

    /** Флаг загрузки данных теста */
    isFetching: true,

    /** Флаг завершения теста (все вопросы отвечены) */
    isFinished: false,

    /** Сообщение об успешной отправке результатов */
    successMessage: null,

    /** Ошибка (загрузки или отправки) */
    error: null,

    /** Валидна ли текущая форма (для вопросов с валидацией) */
    isFormValid: false,

    /** Ключ для localStorage где хранится валидность: `test_${testId}_validity` */
    validityDataKey: '',

    /** Флаг что пользователь в процессе тестирования */
    isTesting: false,
}

// ============================================
// Slice с reducers
// ============================================

const testingSlice = createSlice({
    name: 'testing',
    initialState,
    reducers: {
        /** Устанавливает ID теста */
        setTestId(state, action: PayloadAction<number | null>) {
            state.testId = action.payload
        },

        /** Устанавливает ключ виджета (?wgt=...) */
        setWidgetKey(state, action: PayloadAction<string | null>) {
            state.widgetKey = action.payload
        },

        /** Устанавливает ключ группового теста (?g=...) */
        setGroupKey(state, action: PayloadAction<string | null>) {
            state.groupKey = action.payload
        },

        /** Устанавливает session id виджета (?sid=...) */
        setSid(state, action: PayloadAction<string | null>) {
            state.sid = action.payload
        },

        /**
         * Batch-обновление контекста теста: testId + widget/group/sid за один dispatch.
         * Поля, отсутствующие в payload, не меняются.
         */
        setTestContext(state, action: PayloadAction<TestContextPayload>) {
            const { testId, widgetKey, groupKey, sid } = action.payload;
            if (testId !== undefined) state.testId = testId;
            if (widgetKey !== undefined) state.widgetKey = widgetKey;
            if (groupKey !== undefined) state.groupKey = groupKey;
            if (sid !== undefined) state.sid = sid;
        },

        /** Устанавливает название теста (для title вкладки в SEO) */
        setName(state, action: PayloadAction<string>) {
            state.name = action.payload
        },

        /** Устанавливает флаг "в процессе тестирования" */
        setIsTesting(state, action: PayloadAction<boolean>) {
            state.isTesting = action.payload
        },

        /** Устанавливает цветовую тему фона */
        setBackground(state, action: PayloadAction<string | null>) {
            state.background = action.payload
        },

        /** Устанавливает дополнительные настройки теста (реклама, блоки UI) */
        setAdditional(state, action: PayloadAction<TestAdditional>) {
            state.additional = action.payload
        },

        /** Устанавливает ключ localStorage для хранения ответов */
        setLocalStorageKey(state, action: PayloadAction<string>) {
            state.localStorageKey = action.payload
        },

        /** Устанавливает индекс активного вопроса (0-based) */
        setActiveQuestion(state, action: PayloadAction<number>) {
            state.activeQuestion = action.payload
        },

        /** Устанавливает флаг выбора ответа (для анимации перехода) */
        setSelected(state, action: PayloadAction<boolean>) {
            state.answerState.isSelected = action.payload
        },

        /** Устанавливает флаг завершения теста */
        setIsFinished(state, action: PayloadAction<boolean>) {
            state.isFinished = action.payload
        },

        /** Начало загрузки данных теста */
        fetchTestingStart(state) {
            state.isFetching = true
        },

        /** Ошибка загрузки данных теста */
        fetchTestingError(state, action: PayloadAction<string | null>) {
            state.isFetching = false
            state.error = action.payload
        },

        /** Успешная загрузка данных теста (массив вопросов) */
        fetchTestingLoaded(state, action: PayloadAction<TestQuestion[]>) {
            state.testData = [...action.payload];
            state.isFetching = false;
        },

        /** Обновляет массив ответов пользователя */
        updateAnswers(state, action: PayloadAction<QuestionAnswer[]>) {
            state.answers = [...action.payload]
        },

        /** Сбрасывает все ответы пользователя */
        resetAnswers(state) {
            state.answers = []
        },

        /**
         * Добавляет id вопросов, заполненных авто-ответом (auto_answer).
         * Объединяет без дублей с уже отмеченными.
         */
        addAutoFilledQuestionIds(state, action: PayloadAction<number[]>) {
            state.autoFilledQuestionIds = Array.from(
                new Set([...state.autoFilledQuestionIds, ...action.payload])
            );
        },

        /** Полностью заменяет список авто-заполненных id (восстановление из localStorage) */
        setAutoFilledQuestionIds(state, action: PayloadAction<number[]>) {
            state.autoFilledQuestionIds = [...action.payload];
        },

        /** 
         * Вызывается из resultSlice после успешной отправки ответов на сервер.
         * Сбрасывает флаг isTesting и сохраняет сообщение об успехе.
         */
        answersSent(state, action: PayloadAction<string | string[] | null>) {
            state.successMessage = action.payload;
            state.error = null;
            state.isTesting = false;
        },

        /** Устанавливает ошибку */
        setError(state, action: PayloadAction<string | null>) {
            state.error = action.payload;
        },

        /** 
         * Полный сброс состояния тестирования.
         * Вызывается при начале нового теста или сбросе прогресса.
         * testId не сбрасываем — он нужен для localStorage ключей.
         */
        resetTestingState(state) {
            state.answers = [];
            state.autoFilledQuestionIds = [];
            state.testData = [];
            state.activeQuestion = 0;
            state.answerState.isSelected = false;
            state.error = null;
            state.isFinished = false;
            state.successMessage = null;
        },

        /** Устанавливает валидность формы (для вопросов с обязательными полями) */
        setFormValidity(state, action: PayloadAction<boolean>) {
            state.isFormValid = action.payload
        },

        /** Устанавливает ключ localStorage для хранения валидности */
        setValidityKey(state, action: PayloadAction<string>) {
            state.validityDataKey = action.payload
        },
    }
})

export const {
    setIsFinished,
    fetchTestingStart,
    fetchTestingLoaded,
    fetchTestingError,
    setActiveQuestion,
    setSelected,
    updateAnswers,
    resetAnswers,
    addAutoFilledQuestionIds,
    setAutoFilledQuestionIds,
    setTestId,
    setWidgetKey,
    setGroupKey,
    setSid,
    setTestContext,
    setName,
    setLocalStorageKey,
    answersSent,
    setError,
    setBackground,
    resetTestingState,
    setAdditional,
    setFormValidity,
    setValidityKey,
    setIsTesting,
} = testingSlice.actions

export default testingSlice.reducer

// ============================================
// Thunk: Сохранение валидности в localStorage
// ============================================

/**
 * Сохраняет информацию о валидности текущего вопроса в localStorage.
 * Используется для возврата к невалидному вопросу при продолжении теста.
 */
export const saveValidity = (isValid: boolean) =>
    async (_dispatch: AppDispatch, getState: () => RootState) => {
        const { validityDataKey, activeQuestion } = getState().testing;

        const validityData = {
            activeQuestion: activeQuestion,
            isValid: isValid
        }
        setLocalStorage(validityDataKey, validityData);
    }

// ============================================
// Функция: Синхронизация версии вопросов
// ============================================

/**
 * Синхронизирует версию вопросов теста с сервером.
 * Если тест изменился (хэш версии не совпадает) — сбрасывает весь прогресс пользователя.
 * 
 * Это защита от ситуации когда пользователь начал старую версию теста,
 * а потом тест обновился — его ответы уже неактуальны.
 */
function syncRevisionAndResetIfNeeded(
    testId: number,
    serverRevision: string | null,
    dispatch: AppDispatch
): boolean {
    if (!serverRevision) return false;

    const {
        revisionQuestions: revisionKey,
        answers: answersKey,
        durations: durationsKey,
        validity: validityKey,
        likes: likesKey,
        autoFilled: autoFilledKey,
    } = getTestStorageKeys(testId);

    const storedRevision = localStorage.getItem(revisionKey);

    // Если revision изменился — сбрасываем прогресс
    if (storedRevision !== serverRevision) {
        const hasProgress = !!localStorage.getItem(answersKey)
            || !!localStorage.getItem(durationsKey)
            || !!localStorage.getItem(validityKey)
            || !!localStorage.getItem(likesKey);

        // Очищаем localStorage
        localStorage.removeItem(answersKey);
        localStorage.removeItem(durationsKey);
        localStorage.removeItem(validityKey);
        localStorage.removeItem(likesKey);
        localStorage.removeItem(autoFilledKey);

        // Сохраняем новый revision
        localStorage.setItem(revisionKey, serverRevision);

        if (hasProgress) {
            // Сбрасываем состояние в памяти (Redux store)
            dispatch(resetTestingState());
            dispatch(resetDurationsState());
            dispatch(resetLikesState());
        }
        return true;
    }

    // Если revision не был сохранён раньше — сохраняем
    if (storedRevision == null) {
        localStorage.setItem(revisionKey, serverRevision);
    }

    return false;
}

// ============================================
// Thunk: Инициализация теста
// ============================================

/**
 * Главная функция инициализации теста.
 * Вызывается при загрузке страницы тестирования.
 *
 * @param testId — обязательный ID теста. Приходит пропом в Testing/TestProcess
 *                 Island'е, который читает его из `data-props` контейнера
 *                 `#test-process`.
 *
 * 1. Берёт testId из параметра
 * 2. Устанавливает ключ localStorage
 * 3. Загружает данные теста (вопросы)
 * 4. Загружает настройки теста (фон, реклама)
 */
export const getTest = (testId?: number | null) =>
    (dispatch: AppDispatch): void => {
        if (!testId) {
            console.error('getTest called without testId. Pass it as prop to Testing/TestProcess Island.');
            return;
        }

        const answersDataKey = getTestStorageKeys(testId).answers;
        dispatch(setLocalStorageKey(answersDataKey));

        dispatch(fetchTestData(testId, answersDataKey));
        dispatch(fetchTestBackground(testId));
    }

// ============================================
// Thunk: Загрузка вопросов теста
// ============================================

/**
 * Загружает вопросы теста с API.
 * GET /api/questions?in_process=1&filter[test_id]=${testId}
 * 
 * 1. Проверяет версию вопросов — сбрасывает прогресс если тест изменился
 * 2. Сохраняет вопросы в state
 * 3. Восстанавливает прогресс из localStorage (если есть)
 */
export const fetchTestData = (testId: number, localStorageKey: string) =>
    async (dispatch: AppDispatch): Promise<void> => {
        dispatch(fetchTestingStart())
        try {
            const response = await apiUrl.get(`api/questions?in_process=1&filter[test_id]=${testId}`, { withCredentials: true });
            const testData = response.data.data;
            const serverRevision = response.data.revision_questions;

            // Email авторизованного пользователя предзаполняет форму жалобы на вопрос
            // (форма монтируется лениво при открытии модалки — к тому моменту email уже в сторе).
            const userEmail = response.data.user?.email;
            if (userEmail) {
                dispatch(setReportFormEmail(userEmail));
            }

            // Проверяем revision — сбрасываем прогресс если тест изменился
            syncRevisionAndResetIfNeeded(testId, serverRevision, dispatch);

            dispatch(fetchTestingLoaded(testData));
            dispatch(checkLocalStorage(localStorageKey))
        } catch (error) {
            handleResponseError(error, (data: string | null) => dispatch(fetchTestingError(data)));
        }
    }

// ============================================
// Thunk: Загрузка настроек теста
// ============================================

/**
 * Загружает настройки теста (фон, рекламные блоки).
 * GET /api/tests/${testId}
 */
export const fetchTestBackground = (testId: number) =>
    async (dispatch: AppDispatch): Promise<void> => {
        try {
            const response = await apiUrl.get(`api/tests/${testId}`);
            const background = response.data.data.theme;
            const additional = response.data.data.additional;
            const name: string | undefined = response.data.data.name;
            dispatch(setBackground(background));
            dispatch(setAdditional(additional));
            if (typeof name === 'string') {
                dispatch(setName(name));
            }
        } catch (error) {
            handleResponseError(error, (data: string | null) => dispatch(fetchTestingError(data)));
        }
    }

// ============================================
// Thunk: Восстановление прогресса из localStorage
// ============================================

/**
 * Восстанавливает прогресс тестирования из localStorage.
 * Вызывается после загрузки вопросов теста.
 * 
 * - Если есть сохранённые ответы — загружаем их
 * - Если последний вопрос был невалидным — возвращаемся к нему
 * - Если тест был пройден но не отправлен — ставим на последний вопрос
 * - Иначе — ставим на следующий неотвеченный вопрос
 */
export function checkLocalStorage(localStorageKey: string) {
    return (dispatch: AppDispatch, getState: () => RootState): void => {
        const { testData, validityDataKey, testId } = getState().testing;
        const storedAnswers: QuestionAnswer[] = JSON.parse(localStorage.getItem(localStorageKey) ?? '[]');
        const storedValidity: { activeQuestion?: number; isValid?: boolean } =
            JSON.parse(localStorage.getItem(validityDataKey) ?? '{}');

        // Восстанавливаем список авто-заполненных вопросов (для скрытия «Назад»)
        const autoFilledKey = getTestStorageKeys(testId).autoFilled;
        const storedAutoFilled: number[] = JSON.parse(localStorage.getItem(autoFilledKey) ?? '[]');
        if (storedAutoFilled.length > 0) {
            dispatch(setAutoFilledQuestionIds(storedAutoFilled));
        }

        if (storedAnswers.length > 0) {
            // Сортируем ответы в том же порядке что и вопросы в testData
            // Это критично для корректной работы answers[activeQuestion]
            const sortedAnswers = storedAnswers.sort((a, b) => {
                const indexA = testData.findIndex(q => q.id === a.question_id);
                const indexB = testData.findIndex(q => q.id === b.question_id);
                return indexA - indexB;
            });

            // Если последний ответ был невалидным — возвращаемся к нему
            if (storedValidity.isValid === false && storedValidity.activeQuestion !== undefined) {
                dispatch(setActiveQuestion(storedValidity.activeQuestion))
            } else {
                // Первый не полностью отвеченный вопрос (полнота order учитывается
                // общим хелпером isQuestionFullyAnswered — тем же, что гейтит отправку).
                const firstUnansweredIndex = testData.findIndex(
                    question => !isQuestionFullyAnswered(question, storedAnswers)
                );

                if (firstUnansweredIndex !== -1) {
                    // Есть неотвеченные вопросы - переходим к первому из них
                    dispatch(setActiveQuestion(firstUnansweredIndex))
                } else {
                    // Все вопросы отвечены - ставим на последний
                    dispatch(setActiveQuestion(testData.length - 1))
                }
            }

            dispatch(updateAnswers(sortedAnswers));
        }
    }
}

// ============================================
// Thunk: Обработка клика по ответу
// ============================================

/** Тип значения для radio */
export type RadioValue = string | number;

/** Тип значения для checkbox */
export type CheckboxValue = { name: string | number; checked: boolean };

/** Тип значения для text/number/select */
export type FieldValue = { name: string | number; value: string | number };

/** Объединённый тип для val */
export type AnswerClickValue = RadioValue | CheckboxValue | FieldValue;

/** Типы ответов */
export type AnswerType = 'radio' | 'checkbox' | 'text' | 'number' | 'select' | 'order';

/** Type guard для CheckboxValue */
function isCheckboxValue(val: AnswerClickValue): val is CheckboxValue {
    return val !== null && typeof val === 'object' && 'checked' in val;
}

/** Type guard для FieldValue */
function isFieldValue(val: AnswerClickValue): val is FieldValue {
    return val !== null && typeof val === 'object' && 'value' in val;
}

// ============================================
// Авто-ответ (auto_answer)
// ============================================

/**
 * Определяет 0-based индекс выбранного radio-варианта в списке опций вопроса.
 *
 * Зеркалит логику TestingAnswersList, где значение клика формируется как
 * `opt.val ? opt.val : index`. Поэтому сопоставляем по тому же выражению.
 *
 * @returns индекс варианта или -1, если совпадение не найдено
 */
export function findSelectedOptionIndex(
    question: TestQuestion | undefined,
    val: RadioValue
): number {
    const list = question?.options?.list;
    if (!list) return -1;
    return list.findIndex((opt, index) => (opt.val ? opt.val : index) === val);
}

/**
 * Находит правило auto_answer, соответствующее выбранному варианту.
 *
 * @returns правило или null (нет auto_answer / нет совпадения по answerIndex)
 */
export function findAutoAnswerRule(
    question: TestQuestion | undefined,
    selectedIndex: number
): AutoAnswerRule | null {
    if (!question?.auto_answer || selectedIndex < 0) return null;
    return question.auto_answer.find(rule => rule.answerIndex === selectedIndex) ?? null;
}

/**
 * Сливает ответы-заглушки из правила auto_answer с текущими ответами.
 *
 * Каждый `fill[].questionId` превращается в ответ `{ name: 0, val: value }`.
 * Существующий ответ на тот же вопрос перетирается — это делает повторный
 * выбор варианта-триггера идемпотентным.
 */
export function applyAutoAnswerFill(
    answers: QuestionAnswer[],
    rule: AutoAnswerRule
): QuestionAnswer[] {
    const merged = [...answers];
    for (const { questionId, value } of rule.fill) {
        const filled: QuestionAnswer = {
            question_id: questionId,
            answer: [{ name: 0, val: value }],
        };
        const existingIndex = merged.findIndex(a => a?.question_id === questionId);
        if (existingIndex !== -1) {
            merged[existingIndex] = filled;
        } else {
            merged.push(filled);
        }
    }
    return merged;
}

/**
 * Индекс первого вопроса (начиная с `fromIndex`), на который ещё нет ответа.
 *
 * Используется для авто-перехода: после auto_answer перескакиваем к первому
 * неотвеченному вопросу, как при ручном прохождении.
 *
 * @returns индекс или -1, если все последующие вопросы уже отвечены
 */
export function findNextUnansweredIndex(
    testData: TestQuestion[],
    answers: QuestionAnswer[],
    fromIndex: number
): number {
    for (let i = Math.max(fromIndex, 0); i < testData.length; i++) {
        const question = testData[i];
        const answered = answers.some(
            a => a?.question_id === question.id && a.answer.length > 0
        );
        if (!answered) return i;
    }
    return -1;
}

/**
 * Полностью ли отвечен вопрос — единый источник истины полноты ответа.
 *
 * Важно для order/ranking (Рокич/Люшер/Сонди): «есть запись ответа» ≠ «вопрос
 * завершён». Один клик создаёт ответ `[{name:0,val:X}]`, но ранжирование неполное.
 * Полнота order = выбрано >= submit_after_selections (или все, кроме последнего —
 * он добавляется автоматически). Для остальных типов достаточно непустого ответа.
 *
 * Используется и для чекпоинта восстановления (checkLocalStorage), и для гейта
 * отправки результата (Testing.tsx), чтобы НЕ слать на сервер неполные данные
 * (баг Рокича: один клик в последнем order-вопросе → постинг после resume).
 */
export function isQuestionFullyAnswered(
    question: TestQuestion,
    answers: QuestionAnswer[]
): boolean {
    const answer = answers.find(a => a?.question_id === question.id);
    const answersCount = answer?.answer?.length ?? 0;
    if (answersCount === 0) {
        return false;
    }

    const firstOption = question.options?.list?.[0];
    if (firstOption?.type === 'order') {
        const submitLimit = resolveOrderConfig(question.question, question.options).submitAfterSelections;
        if (submitLimit !== undefined) {
            return answersCount >= submitLimit;
        }
        const totalOptions = question.options?.list?.length ?? 0;
        return answersCount >= totalOptions - 1;
    }

    return true;
}

/**
 * Готов ли результат к отправке: все вопросы полностью отвечены.
 * Пустой testData — не готов (нечего отправлять).
 */
export function areAllQuestionsAnswered(
    testData: TestQuestion[],
    answers: QuestionAnswer[]
): boolean {
    return testData.length > 0 && testData.every(question => isQuestionFullyAnswered(question, answers));
}

/**
 * Главная функция обработки ответа пользователя.
 * Вызывается при клике по варианту ответа.
 * 
 * Типы ответов:
 * - radio: один вариант, val = выбранное значение
 * - checkbox: множественный выбор, val = { name, checked }
 * - text/number/select: поля ввода, val = { name, value }
 * 
 * 1. Формирует объект ответа в зависимости от типа
 * 2. Сохраняет в localStorage
 * 3. Обновляет state
 * 4. Через 300мс переходит к следующему вопросу (если нет кнопки "Далее")
 */
export function answerClick(
    val: AnswerClickValue,
    question_id: number,
    currentQuestionNextBtnShow: boolean | undefined,
    type: AnswerType = 'radio'
) {
    return (dispatch: AppDispatch, getState: () => RootState): void => {
        const state = getState().testing;
        const { answers, localStorageKey } = getState().testing;

        // Проверка синхронизации localStorage
        checkIfLocalStorageDoesNotContainCurrentState(localStorageKey, answers)

        let answersArray: AnswerValue[] = [];

        // Формируем ответ в зависимости от типа
        switch (type) {
            // Radio: один вариант ответа
            case 'radio': {
                // val — это string | number для radio
                const radioVal = val as RadioValue;
                const newRadio: AnswerValue = { name: 0, val: radioVal };
                answersArray = [newRadio];
                break;
            }

            // Order: последовательный выбор (тесты Люшера, Рокича)
            // Формат ответов: просто массив значений [1, 2, 4, ...] (как в legacy)
            case 'order': {
                // val — это string | number для order
                const orderVal = val as RadioValue;
                
                // Находим ответ по question_id (не по индексу!)
                const currentAnswer = answers.find(a => a?.question_id === question_id);

                // Добавляем к существующим ответам (накапливаем порядок выбора)
                // Используем индекс в массиве как ключ для сохранения порядка
                if (currentAnswer) {
                    // Фильтруем заглушки и добавляем новое значение
                    const withoutPlaceholders = currentAnswer.answer.filter(a => a.val !== null);
                    const nextIndex = withoutPlaceholders.length;
                    answersArray = [...withoutPlaceholders, { name: nextIndex, val: orderVal }];
                } else {
                    answersArray = [{ name: 0, val: orderVal }];
                }
                break;
            }

            // Checkbox: множественный выбор
            case 'checkbox': {
                if (!isCheckboxValue(val)) break;
                const newCheckbox: AnswerValue = { name: val.name, val: 1 };

                // Находим ответ по question_id (не по индексу!)
                const currentAnswer = answers.find(a => a?.question_id === question_id);

                if (val.checked) {
                    // Добавляем к существующим ответам
                    if (currentAnswer) {
                        // Фильтруем заглушки
                        const withoutPlaceholders = currentAnswer.answer.filter(a => a.name !== null);
                        answersArray = [...withoutPlaceholders, newCheckbox];
                    } else {
                        answersArray = [newCheckbox];
                    }
                } else {
                    // Убираем из ответов (используем currentAnswer сверху)
                    if (currentAnswer) {
                        answersArray = currentAnswer.answer.filter(answer => answer.name !== newCheckbox.name);
                    }
                }
                break;
            }

            // Text, Number, Select: поля ввода
            case 'number':
            case 'text':
            case 'select': {
                if (!isFieldValue(val)) break;
                // Для number конвертируем в число, для остальных — строка
                // Пустую строку НЕ конвертируем в 0 (чтобы можно было стереть 0 из поля)
                const newField: AnswerValue = {
                    name: val.name,
                    val: type === 'number'
                        ? (val.value === '' ? '' : Number(val.value))
                        : val.value
                };

                // Находим ответ по question_id (не по индексу!)
                const currentAnswer = answers.find(a => a?.question_id === question_id);

                if (currentAnswer) {
                    // Ищем существующий ответ с таким же name
                    const existingAnswerIndex = currentAnswer.answer.findIndex(
                        answer => answer.name === newField.name
                    );

                    if (existingAnswerIndex !== -1) {
                        // Обновляем существующий
                        answersArray = currentAnswer.answer.map((answer, index) =>
                            index === existingAnswerIndex ? newField : answer
                        );
                    } else {
                        // Добавляем новый (фильтруем заглушки)
                        const withoutPlaceholders = currentAnswer.answer.filter(a => a.name !== null);
                        answersArray = [...withoutPlaceholders, newField];
                    }
                } else {
                    answersArray = [newField]
                }
                break;
            }
        }

        // Формируем объект ответа на вопрос
        const newAnswer: QuestionAnswer = {
            question_id: question_id,
            answer: answersArray
        };

        // Базовый массив ответов с учётом текущего вопроса (обновляем на месте,
        // чтобы порядок и индексы не сдвинулись)
        const baseAnswers = [...answers];
        const existingIndex = baseAnswers.findIndex(a => a?.question_id === question_id);
        if (existingIndex !== -1) {
            baseAnswers[existingIndex] = newAnswer;
        } else {
            baseAnswers.push(newAnswer);
        }

        // Авто-ответ (auto_answer): при совпадении выбранного варианта с
        // правилом — дозаполняем ответы на связанные вопросы.
        let finalAnswers = baseAnswers;
        let autoAnswerApplied = false;
        let autoFilledIds: number[] = [];
        if (type === 'radio' && answersArray.length > 0) {
            const currentQuestion = state.testData.find(q => q.id === question_id);
            const selectedIndex = findSelectedOptionIndex(currentQuestion, val as RadioValue);
            const autoRule = findAutoAnswerRule(currentQuestion, selectedIndex);
            if (autoRule) {
                finalAnswers = applyAutoAnswerFill(baseAnswers, autoRule);
                autoFilledIds = autoRule.fill.map(item => item.questionId);
                autoAnswerApplied = true;
            }
        }

        // Персистим ответы в localStorage
        if (answersArray.length === 0) {
            // Пустой ответ — удаляем вопрос из localStorage
            const storedData = JSON.parse(localStorage.getItem(localStorageKey) ?? '[]');
            const filteredData = storedData.filter((item: QuestionAnswer) => item.question_id !== question_id);
            localStorage.setItem(localStorageKey, JSON.stringify(filteredData));
        } else if (autoAnswerApplied) {
            // Авто-заполнение — сохраняем весь массив целиком
            localStorage.setItem(localStorageKey, JSON.stringify(finalAnswers));
        } else {
            // Обычное сохранение одного ответа
            saveDataToLocalStorage(localStorageKey, newAnswer, () => {
                // НЕ используем callback - обновляем Redux вручную ниже
            });
        }

        // Обновляем Redux вручную (сохраняем порядок ответов)
        dispatch(updateAnswers(finalAnswers));

        // Запоминаем авто-заполненные вопросы (для скрытия «Назад») + персистим
        if (autoFilledIds.length > 0) {
            const autoFilledKey = getTestStorageKeys(state.testId).autoFilled;
            const stored: number[] = JSON.parse(localStorage.getItem(autoFilledKey) ?? '[]');
            const merged = Array.from(new Set([...stored, ...autoFilledIds]));
            localStorage.setItem(autoFilledKey, JSON.stringify(merged));
            dispatch(addAutoFilledQuestionIds(autoFilledIds));
        }

        // Отмечаем что ответ выбран (для анимации)
        dispatch(setSelected(true));

        // Для типа 'order' НЕ делаем автоматический переход
        // OrderAnswer сам управляет переключением через onComplete
        if (type === 'order') {
            return;
        }

        // Через 300мс переходим к следующему вопросу (для radio, checkbox и т.д.)
        const timeout = window.setTimeout(() => {
            // Кнопка "Далее" сама управляет переходом — авто-переход не делаем
            if (!currentQuestionNextBtnShow) {
                if (autoAnswerApplied) {
                    // Перескакиваем к первому неотвеченному вопросу после текущего;
                    // если таких нет — сразу к результату
                    const nextIndex = findNextUnansweredIndex(
                        state.testData,
                        finalAnswers,
                        state.activeQuestion + 1
                    );
                    if (nextIndex === -1) {
                        dispatch(setIsFinished(true));
                    } else {
                        dispatch(setActiveQuestion(nextIndex));
                        dispatch(setSelected(false));
                    }
                } else if (state.activeQuestion + 1 === state.testData.length) {
                    // Последний вопрос — завершаем тест
                    dispatch(setIsFinished(true));
                } else {
                    // Не последний — переходим к следующему
                    dispatch(setActiveQuestion(state.activeQuestion + 1));
                    dispatch(setSelected(false));
                }
            }
            window.clearTimeout(timeout)
        }, 300)
    }
}
